import { supabase } from '../supabaseClient';
import { getCachedUserAndProfile } from '../utils/userCache';
import { logAudit, logAuditBatch } from './auditLog';
import { createNotification, createBatchNotifications, sendEmailNotification, buildEmailHtml, sendTeamsNotification } from './notifications';
import { isNotificationEnabled, isNotificationEnabledBatch } from './notificationPreferences';
import { isWorkItemNoSearch, parseWorkItemNoSearch, compareWorkItemSiblings } from '../constants/workItem';
import { parseAttachmentIds, stripAttachmentMarkup } from '../utils/workItemAttachmentMarkup';

// audit 不可讓寫入失敗 → 一律 guard
const safeAudit = async (...args) => {
  try { await logAudit(...args); } catch (e) { console.warn('[workItems] audit failed', e); }
};
const safeAuditBatch = async (logs) => {
  try { await logAuditBatch(logs); } catch (e) { console.warn('[workItems] batch audit failed', e); }
};

// 指派通知(比照 ticketAssignees:站內 + Email + Teams,尊重偏好,不通知自己)
const notifyWorkItemAssignment = async (workItemId, userId, actorId) => {
  try {
    if (!userId || userId === actorId) return;
    const { data: wi } = await supabase
      .from('work_items')
      .select('title, work_item_no, projects:project_id (name)')
      .eq('id', workItemId).single();
    const projectName = wi?.projects?.name || '';
    const label = wi?.work_item_no
      ? `${projectName} #${wi.work_item_no} ${wi.title || ''}`.trim()
      : (wi?.title || `工作項目 #${workItemId}`);
    const prefs = await isNotificationEnabled(userId, 'ticket_assigned');
    if (prefs.in_app) {
      createNotification({
        user_id: userId, type: 'work_item_assigned',
        title: '你被指派了一個工作項目', message: label,
        resource_type: 'work_item', resource_id: String(workItemId), actor_id: actorId,
      });
    }
    const { data: prof } = await supabase.from('profiles').select('email').eq('id', userId).maybeSingle();
    const email = prof?.email;
    if (prefs.email && email) {
      sendEmailNotification({
        to: email, subject: `[Sonice] 你被指派了一個工作項目 — ${label}`,
        html: buildEmailHtml({ title: '你被指派了一個工作項目', message: label, ticketId: workItemId, ticketLabel: label, url: workItemUrl(workItemId) }),
        type: 'work_item_assigned',
      });
    }
    sendTeamsNotification({ user_id: userId, event_type: 'ticket_assigned', title: '你被指派了一個工作項目', message: label, ticketId: workItemId, targetEmail: email || null, url: workItemUrl(workItemId) });
  } catch (e) { console.warn('[workItems] notify failed', e); }
};

// 批次改 Owner 的彙整通知:一則「你被設為 N 個工作項目的 Owner」(站內 1 + Email/Teams 各 1),不通知操作者本人
const notifyWorkItemsOwnerBatch = async (ownerId, ids, actorId) => {
  try {
    if (!ownerId || ownerId === actorId || !ids?.length) return;
    const msg = `你被設為 ${ids.length} 個工作項目的 Owner`;
    const prefs = await isNotificationEnabled(ownerId, 'ticket_assigned');
    if (prefs.in_app) {
      createNotification({
        user_id: ownerId, type: 'work_item_assigned',
        title: '你被指派為 Owner', message: msg,
        resource_type: 'work_item', resource_id: String(ids[0]), actor_id: actorId,
      });
    }
    const { data: prof } = await supabase.from('profiles').select('email').eq('id', ownerId).maybeSingle();
    const email = prof?.email;
    if (prefs.email && email) {
      sendEmailNotification({
        to: email, subject: `[Sonice] ${msg}`,
        html: buildEmailHtml({ title: '你被指派為 Owner', message: msg, ticketId: ids[0], ticketLabel: msg, url: workItemUrl(ids[0]) }),
        type: 'work_item_assigned',
      });
    }
    sendTeamsNotification({ user_id: ownerId, event_type: 'ticket_assigned', title: '你被指派為 Owner', message: msg, ticketId: ids[0], targetEmail: email || null, url: workItemUrl(ids[0]) });
  } catch (e) { console.warn('[workItems] batch owner notify failed', e); }
};

// ─────────────── Groups(原 Phase,純分類容器) ───────────────
export const getWorkItemGroups = async (projectId, { companyId } = {}) => {
  try {
    let q = supabase.from('work_item_groups').select('*').is('deleted_at', null).order('sort_order', { ascending: true });
    if (projectId) q = q.eq('project_id', projectId);
    if (companyId) q = q.eq('company_id', companyId);
    const { data, error } = await q;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

export const createWorkItemGroup = async ({ project_id, company_id, department_id, name, sort_order = 0 }) => {
  try {
    const { data, error } = await supabase
      .from('work_item_groups')
      .insert({ project_id, company_id, department_id, name, sort_order })
      .select('*').single();
    if (error) throw error;
    await safeAudit('create', 'work_item_group', data.id, data.name, { after: data });
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

export const updateWorkItemGroup = async (id, updates) => {
  try {
    const { data, error } = await supabase.from('work_item_groups')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id).select('*').single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

// 依傳入順序重寫群組 sort_order
export const updateWorkItemGroupOrder = async (orderedIds) => {
  try {
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from('work_item_groups').update({ sort_order: i }).eq('id', id)));
    return { error: null };
  } catch (error) { return { error }; }
};

export const deleteWorkItemGroup = async (id) => {
  try {
    const { error } = await supabase.from('work_item_groups')
      .update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (error) { return { error }; }
};

// 各群組頂層工作項目「含所有狀態」的總筆數(head count,不撈 row)。
// 用途:區分「群組真的空」vs「群組有東西但全被隱藏(如全 closed)」→ 決定要不要隱藏群組。
const WI_UNGROUPED = '__ungrouped__';
export const getWorkItemGroupTotals = async (projectId, groupIds = [], { companyId } = {}) => {
  try {
    if (!projectId) return { data: {}, error: null };
    const keys = [...groupIds, WI_UNGROUPED];
    const entries = await Promise.all(keys.map(async (gid) => {
      let q = supabase.from('work_items').select('*', { count: 'exact', head: true })
        .eq('project_id', projectId).is('deleted_at', null).is('parent_id', null);
      if (companyId) q = q.eq('company_id', companyId);
      q = gid === WI_UNGROUPED ? q.is('group_id', null) : q.eq('group_id', gid);
      const { count } = await q;
      return [gid, count || 0];
    }));
    return { data: Object.fromEntries(entries), error: null };
  } catch (error) { return { data: {}, error }; }
};

// ─────────────── Work Items ───────────────
// 專案內列表只撈活躍項目;closed 會無限累積,改由「過往工作項目」走 searchWorkItems 分頁查詢
export const getWorkItems = async (projectId, { companyId } = {}) => {
  try {
    let q = supabase
      .from('work_items')
      .select('*, projects:project_id (id, name, color), assignees:work_item_assignees(user_id, assigned_at, assigned_by)')
      .is('deleted_at', null)
      .order('display_order', { ascending: true })
      .order('work_item_no', { ascending: false });
    if (projectId) q = q.eq('project_id', projectId);
    if (companyId) q = q.eq('company_id', companyId);
    q = q.neq('status', 'closed');
    const { data, error } = await q;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

const nextWorkItemNo = async (companyId) => {
  // 公司內流水號(uq_work_items_company_no),比照舊 tickets 的公司級編號
  // 子項 work_item_no 為 NULL;DESC 預設 NULLS FIRST 會排到最前 → 必須排除 NULL 才拿得到真正最大號
  const { data } = await supabase.from('work_items')
    .select('work_item_no').eq('company_id', companyId)
    .not('work_item_no', 'is', null)
    .order('work_item_no', { ascending: false }).limit(1);
  return (data?.[0]?.work_item_no || 0) + 1;
};

// deep-link(?wi=<id>)用:撈該項目所屬專案/公司,供 BetaLayout 同步左側 sidebar 選取
export const getWorkItemProjectRef = async (id) => {
  const { data, error } = await supabase.from('work_items')
    .select('project_id, company_id, work_item_no').eq('id', id).maybeSingle();
  return { data, error };
};

// deep-link(?cid=<公司>&wi=<work_item_no>)用:以「公司內顯示編號」解回 work_item(mirror getTicketByNo)。
// work_item_no 只發給頂層項目 + 公司內唯一(uq_work_items_company_no),故 (work_item_no, company_id) 唯一。
export const getWorkItemByNo = async (workItemNo, companyId) => {
  const { data, error } = await supabase.from('work_items')
    .select('id, project_id, company_id')
    .eq('work_item_no', workItemNo).eq('company_id', companyId).maybeSingle();
  return { data, error };
};

export const createWorkItem = async (data) => {
  try {
    // 編號只發給頂層;子項(有 parent_id)一律 null,不吃號
    const isChild = !!data.parent_id;
    const work_item_no = isChild ? null : await nextWorkItemNo(data.company_id);
    const insertData = {
      work_item_no,
      project_id: data.project_id,
      group_id: data.group_id || null,
      parent_id: data.parent_id || null,
      company_id: data.company_id,
      department_id: data.department_id,
      creator_id: data.creator_id,
      type: data.type || 'task',
      title: data.title,
      description: data.description || null,
      status: data.status || 'open',
      priority: data.priority ?? 1,
      role: data.role || null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      estimated_hours: data.estimated_hours ?? null,
      last_activity_at: new Date().toISOString(),
      last_activity_by: data.creator_id,
    };
    const { data: wi, error } = await supabase.from('work_items').insert(insertData).select('*').single();
    if (error) throw error;

    if (data.assignee_ids?.length) {
      const rows = data.assignee_ids.map((uid) => ({ work_item_id: wi.id, user_id: uid, assigned_by: data.creator_id }));
      await supabase.from('work_item_assignees').insert(rows);
      data.assignee_ids.forEach((uid) => notifyWorkItemAssignment(wi.id, uid, data.creator_id));
    }
    await safeAudit('create', 'work_item', wi.id, wi.title, { after: wi });
    return { data: wi, error: null };
  } catch (error) { return { data: null, error }; }
};

export const updateWorkItem = async (id, updates) => {
  try {
    // 狀態變更需記錄前值 → 先撈舊狀態
    const wantsStatus = Object.prototype.hasOwnProperty.call(updates, 'status');
    let beforeStatus = null;
    if (wantsStatus) {
      const { data: cur } = await supabase.from('work_items').select('status').eq('id', id).single();
      beforeStatus = cur?.status ?? null;
    }
    // 比照 ticket:改動 work_item 本身 = 一次活動 → 同步 bump last_activity
    const nowIso = new Date().toISOString();
    const { profile } = await getCachedUserAndProfile(supabase);
    const { data, error } = await supabase.from('work_items')
      .update({
        ...updates, updated_at: nowIso, last_activity_at: nowIso,
        ...(profile?.id ? { last_activity_by: profile.id } : {}),
      })
      .eq('id', id).select('*').single();
    if (error) throw error;
    if (wantsStatus && beforeStatus !== data.status) {
      await safeAudit('status_change', 'work_item', id, data.title, { before: { status: beforeStatus }, after: { status: data.status } });
    } else {
      await safeAudit('update', 'work_item', id, data.title, { after: data });
    }
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

// QA 驗收:逐條更新單一驗收條件的結果(pass/fail;status=null 表示取消勾選)。
// 讀當前 acceptance_results → merge 這一條 {status,by,at} → 寫回。
// by 從 server session 拿(可信,不靠前端傳);只 merge 這條、不整包覆蓋(不蓋掉別人勾的)。
// 只動 acceptance_results,不碰 acceptance_criteria(題目來源是 specforge,QA 不改題)。
export const setWorkItemAcceptanceResult = async (id, criterionId, status) => {
  try {
    const { data: cur, error: readErr } = await supabase.from('work_items')
      .select('acceptance_results').eq('id', id).single();
    if (readErr) throw readErr;
    const { profile } = await getCachedUserAndProfile(supabase);
    const nowIso = new Date().toISOString();
    const results = { ...(cur?.acceptance_results || {}) };
    if (status == null) delete results[criterionId];
    else results[criterionId] = { status, by: profile?.id || null, at: nowIso };
    const { data, error } = await supabase.from('work_items')
      .update({
        acceptance_results: results,
        updated_at: nowIso, last_activity_at: nowIso,
        ...(profile?.id ? { last_activity_by: profile.id } : {}),
      })
      .eq('id', id).select('*').single();
    if (error) throw error;
    await safeAudit('update', 'work_item', id, data.title, { after: { acceptance_result: { criterionId, status } } });
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

/**
 * 子項拖曳排序用:撈某母項底下「全部」子項,含畫面上被過濾掉的 closed。
 * 沒有這步就只能把「可見的」順序寫回去,被藏起來的 closed 子項會保留舊的
 * display_order,之後 closed 子項重新出現時順序就錯亂了。
 */
export const getAllChildrenForOrder = async (parentId) => {
  try {
    if (!parentId) return { data: [], error: null };
    const { data, error } = await supabase.from('work_items')
      .select('id, title, status, display_order')
      .eq('parent_id', parentId).is('deleted_at', null);
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

// 依傳入順序重寫 work_items display_order(同群組內排序)
export const updateWorkItemOrder = async (orderedIds) => {
  try {
    await Promise.all(orderedIds.map((id, i) =>
      supabase.from('work_items').update({ display_order: i }).eq('id', id)));
    return { error: null };
  } catch (error) { return { error }; }
};

export const deleteWorkItem = async (id) => {
  try {
    const { error } = await supabase.from('work_items')
      .update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    await safeAudit('delete', 'work_item', id, null, {});
    return { error: null };
  } catch (error) { return { error }; }
};

// 全系統查詢:跨專案 work_items + 篩選 + server-side 分頁(比照原始 Ticket 查詢)
// 回傳 { data, count }(count = 符合條件總數,供分頁計算)。只列頂層工作項目。
// 也供「過往工作項目」(statuses=['closed'])共用:
// - groupIds:群組篩選,可含 WI_UNGROUPED(= group_id 為 null);空陣列 = 不篩
// - requireAssigneeId:限定指派給該使用者(我的過往),與 assigneeIds 篩選取交集
// 留言命中的 id 上限(見下方 searchWorkItems 內註解)
const COMMENT_MATCH_LIMIT = 500;

export const searchWorkItems = async ({
  companyId, search = '', statuses = [], priorities = [], types = [],
  assigneeIds = [], projectIds = [], groupIds = [], requireAssigneeId = null,
  page = 1, pageSize = 20, viewerUserId = null,
  sortBy = 'work_item_no', sortOrder = 'desc',
} = {}) => {
  try {
    // 指派人篩選 → 先解析出對應 work_item_id 集合
    const idsAssignedTo = async (userIds) => {
      const { data: a } = await supabase.from('work_item_assignees')
        .select('work_item_id').in('user_id', userIds);
      return [...new Set((a || []).map((r) => r.work_item_id))];
    };
    let restrictIds = null;
    if (assigneeIds.length) restrictIds = await idsAssignedTo(assigneeIds);
    if (requireAssigneeId) {
      const mine = await idsAssignedTo([requireAssigneeId]);
      restrictIds = restrictIds ? mine.filter((id) => restrictIds.includes(id)) : mine;
    }
    if (restrictIds && !restrictIds.length) return { data: [], count: 0, error: null };
    let q = supabase
      .from('work_items')
      .select('*, projects:project_id (id, name, color), group:group_id (id, name), assignees:work_item_assignees(user_id, assigned_at)', { count: 'exact' })
      .is('deleted_at', null)
      .is('parent_id', null);
    if (companyId) q = q.eq('company_id', companyId);
    if (restrictIds) q = q.in('id', restrictIds);
    const term = (search || '').trim();
    if (term) {
      if (isWorkItemNoSearch(term)) {
        q = q.eq('work_item_no', parseWorkItemNoSearch(term));
      } else {
        const like = `%${term}%`;
        const conds = [`title.ilike.${like}`, `description.ilike.${like}`];
        // 留言內容也算命中(比照 Ticket 的 extendedSearch:tickets.js:396-417)。
        // id 清單會進 URL,太長會撞 414,故設上限;超出的部分仍可能被 title/description 命中。
        const { data: cm } = await supabase.from('work_item_comments')
          .select('work_item_id').ilike('content', like).limit(COMMENT_MATCH_LIMIT);
        const cmIds = [...new Set((cm || []).map((r) => r.work_item_id))].filter(Boolean);
        if (cmIds.length) conds.push(`id.in.(${cmIds.join(',')})`);
        q = q.or(conds.join(','));
      }
    }
    if (statuses.length) q = q.in('status', statuses);
    if (priorities.length) q = q.in('priority', priorities);
    if (types.length) q = q.in('type', types);
    if (projectIds.length) q = q.in('project_id', projectIds);
    if (groupIds.length) {
      const realIds = groupIds.filter((g) => g !== WI_UNGROUPED);
      const conds = [];
      if (realIds.length) conds.push(`group_id.in.(${realIds.join(',')})`);
      if (groupIds.includes(WI_UNGROUPED)) conds.push('group_id.is.null');
      q = q.or(conds.join(',')); // 與關鍵字的 or() 並存時 PostgREST 以 AND 結合
    }
    const fromIdx = (page - 1) * pageSize;
    // 表頭可切換排序(比照 Ticket);null 值殿後,非編號排序時以編號當平手鍵
    const ascending = sortOrder === 'asc';
    q = q.order(sortBy, { ascending, nullsFirst: false });
    if (sortBy !== 'work_item_no') q = q.order('work_item_no', { ascending: false });
    q = q.range(fromIdx, fromIdx + pageSize - 1);
    const { data, count, error } = await q;
    if (error) throw error;
    // 未讀判斷:該 work_item 的 last_activity_at 比使用者最後檢視時間新(或從沒看過)→ 未讀
    let rows = data || [];
    if (viewerUserId && rows.length) {
      const { data: views } = await getWorkItemViews(rows.map((r) => r.id), viewerUserId);
      const viewMap = new Map((views || []).map((v) => [v.work_item_id, v.last_viewed_at]));
      rows = rows.map((r) => {
        // 基準 = last_activity_at(只有真正協作活動才動;舊資料 fallback updated_at)
        const activityAt = r.last_activity_at || r.updated_at;
        return {
          ...r,
          has_new_activity: !viewMap.has(r.id)
            || (activityAt && new Date(activityAt) > new Date(viewMap.get(r.id))),
        };
      });
    }
    return { data: rows, count: count || 0, error: null };
  } catch (error) { return { data: [], count: 0, error }; }
};

// 記錄使用者檢視 work_item 的時間(UPSERT),供全系統查詢列表判斷未讀
export const upsertWorkItemView = async (workItemId, userId) => {
  const { error } = await supabase
    .from('work_item_views')
    .upsert(
      { work_item_id: workItemId, user_id: userId, last_viewed_at: new Date().toISOString() },
      { onConflict: 'work_item_id,user_id' },
    );
  return { error };
};

// 批次取得指定 work_items 的 last_viewed_at(供列表判斷未讀狀態)
export const getWorkItemViews = async (workItemIds, userId) => {
  if (!workItemIds.length || !userId) return { data: [] };
  const { data, error } = await supabase
    .from('work_item_views')
    .select('work_item_id, last_viewed_at')
    .eq('user_id', userId)
    .in('work_item_id', workItemIds);
  if (error) console.error('Error fetching work item views:', error);
  return { data: data || [] };
};

// 單筆 work_item(含指派 + 專案)
export const getWorkItem = async (id) => {
  try {
    const { data, error } = await supabase
      .from('work_items')
      .select('*, projects:project_id (id, name, color, autopilot_config), assignees:work_item_assignees(user_id, assigned_at)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

// 子項(某 work_item 的直屬子項)
export const getWorkItemChildren = async (parentId) => {
  try {
    const { data, error } = await supabase
      .from('work_items')
      .select('*, assignees:work_item_assignees(user_id, assigned_at)')
      .eq('parent_id', parentId)
      .is('deleted_at', null);
    if (error) throw error;
    // 排序交給 compareWorkItemSiblings,跟列表(buildWorkItemTree)用同一支 ——
    // 原本這裡的 tiebreaker 是 work_item_no,但子項的 work_item_no 一律是 NULL,
    // 等於沒有 tiebreaker,兩邊會排出不一樣的順序。
    return { data: (data || []).sort(compareWorkItemSiblings), error: null };
  } catch (error) { return { data: [], error }; }
};

// ─────────────── 指派(Owner = 最早 assigned_at) ───────────────
export const addWorkItemAssignee = async (workItemId, userId, assignedBy) => {
  try {
    const { error } = await supabase.from('work_item_assignees')
      .upsert({ work_item_id: workItemId, user_id: userId, assigned_by: assignedBy }, { onConflict: 'work_item_id,user_id' });
    if (error) throw error;
    await safeAudit('assign', 'work_item', workItemId, null, { user_id: userId });
    notifyWorkItemAssignment(workItemId, userId, assignedBy);
    return { error: null };
  } catch (error) { return { error }; }
};

export const removeWorkItemAssignee = async (workItemId, userId) => {
  try {
    const { error } = await supabase.from('work_item_assignees')
      .delete().eq('work_item_id', workItemId).eq('user_id', userId);
    if (error) throw error;
    await safeAudit('unassign', 'work_item', workItemId, null, { user_id: userId });
    return { error: null };
  } catch (error) { return { error }; }
};

// 設為 Owner:把該人 assigned_at 調到比現有最早再早,使其排序第一
export const setWorkItemOwner = async (workItemId, newOwnerId, assignedBy) => {
  try {
    const { data: rows } = await supabase.from('work_item_assignees')
      .select('user_id, assigned_at').eq('work_item_id', workItemId).order('assigned_at', { ascending: true });
    const earliest = rows?.[0]?.assigned_at ? new Date(rows[0].assigned_at).getTime() : Date.now();
    const ownerTime = new Date(earliest - 1000).toISOString();
    const { error } = await supabase.from('work_item_assignees')
      .upsert({ work_item_id: workItemId, user_id: newOwnerId, assigned_by: assignedBy, assigned_at: ownerTime }, { onConflict: 'work_item_id,user_id' });
    if (error) throw error;
    await safeAudit('assignment_change', 'work_item', workItemId, null, { owner_id: newOwnerId });
    notifyWorkItemAssignment(workItemId, newOwnerId, assignedBy);
    return { error: null };
  } catch (error) { return { error }; }
};

// ─────────────── 批次操作(我的工作項目批次編輯) ───────────────
// 批次改狀態:一次 UPDATE ... WHERE id IN (...)
export const updateWorkItemsStatus = async (ids, status, actorId) => {
  try {
    if (!ids?.length) return { error: null };
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('work_items')
      .update({ status, updated_at: nowIso, last_activity_at: nowIso, ...(actorId ? { last_activity_by: actorId } : {}) })
      .in('id', ids);
    if (error) throw error;
    // 每個 work_item 各記一筆(resource_id 各自 id,查單一項目查得到)
    const auditRows = ids.map((id) => ({ action: 'status_change', resourceType: 'work_item', resourceId: id, changes: { after: { status } }, metadata: { batch: true } }));
    // 改成 closed 時,一併結束底下未完成子項(對齊詳細內頁 cascade,避免子項卡未完成變孤兒浮到未分組)
    if (status === 'closed') {
      const { data: subs } = await supabase.from('work_items')
        .select('id').in('parent_id', ids).eq('is_completed', false).is('deleted_at', null);
      const subIds = (subs || []).map((s) => s.id);
      if (subIds.length) {
        const { error: subErr } = await supabase.from('work_items')
          .update({ is_completed: true, status: 'closed', completed_at: nowIso, updated_at: nowIso, last_activity_at: nowIso, ...(actorId ? { last_activity_by: actorId } : {}) })
          .in('id', subIds);
        if (subErr) throw subErr;
        subIds.forEach((id) => auditRows.push({ action: 'status_change', resourceType: 'work_item', resourceId: id, changes: { after: { status: 'closed', is_completed: true } }, metadata: { batch: true, cascade: true } }));
      }
    }
    await safeAuditBatch(auditRows);
    return { error: null };
  } catch (error) { return { error }; }
};

// 批次設 Owner:一次撈所有 assignees → 每個算最早時間 → 一次批次 upsert(原 owner 降協作者保留)
export const setWorkItemsOwner = async (ids, ownerId, actorId) => {
  try {
    if (!ids?.length) return { error: null };
    const { data: rows } = await supabase.from('work_item_assignees')
      .select('work_item_id, assigned_at').in('work_item_id', ids);
    const earliest = {};
    (rows || []).forEach((r) => {
      const t = new Date(r.assigned_at).getTime();
      if (earliest[r.work_item_id] == null || t < earliest[r.work_item_id]) earliest[r.work_item_id] = t;
    });
    const upserts = ids.map((wid) => {
      const base = earliest[wid] != null ? earliest[wid] : Date.now();
      return { work_item_id: wid, user_id: ownerId, assigned_by: actorId, assigned_at: new Date(base - 1000).toISOString() };
    });
    const { error } = await supabase.from('work_item_assignees')
      .upsert(upserts, { onConflict: 'work_item_id,user_id' });
    if (error) throw error;
    await safeAuditBatch(ids.map((id) => ({ action: 'assignment_change', resourceType: 'work_item', resourceId: id, changes: { owner_id: ownerId }, metadata: { batch: true } })));
    notifyWorkItemsOwnerBatch(ownerId, ids, actorId);
    return { error: null };
  } catch (error) { return { error }; }
};

// 批次新增交付紀錄:一次 INSERT 多筆(每個 work_item 一筆相同內容)
export const createWorkItemDeliveryLogs = async (rows) => {
  try {
    if (!rows?.length) return { error: null };
    const { error } = await supabase.from('work_item_delivery_logs').insert(rows);
    if (error) throw error;
    return { error: null };
  } catch (error) { return { error }; }
};

// ─────────────── 附件 ───────────────
const WI_ATTACH_BUCKET = 'ticket-attachments'; // 重用現有 bucket,用 wi/ 前綴區隔

export const getWorkItemAttachments = async (workItemId) => {
  try {
    const { data, error } = await supabase
      .from('work_item_attachments').select('*')
      .eq('work_item_id', workItemId).eq('source', 'item').order('created_at', { ascending: false });
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

export const uploadWorkItemAttachment = async (workItemId, uploadedBy, file, deliveryLogId = null) => {
  try {
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const sub = deliveryLogId ? 'delivery/' : '';
    const filePath = `wi/${workItemId}/${sub}${Date.now()}_${safe}`;
    const { error: upErr } = await supabase.storage.from(WI_ATTACH_BUCKET).upload(filePath, file, { cacheControl: '3600', upsert: false });
    if (upErr) throw upErr;
    const { data, error } = await supabase.from('work_item_attachments').insert({
      work_item_id: workItemId, uploaded_by: uploadedBy,
      file_name: file.name, file_path: filePath, file_size: file.size, file_type: file.type,
      delivery_log_id: deliveryLogId, source: deliveryLogId ? 'delivery' : 'item',
    }).select('*').single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

// downloadName 有值時,signed URL 會帶 Content-Disposition: attachment,強制下載
export const getWorkItemAttachmentUrl = async (filePath, downloadName) => {
  try {
    const opts = downloadName ? { download: downloadName } : undefined;
    const { data, error } = await supabase.storage.from(WI_ATTACH_BUCKET).createSignedUrl(filePath, 3600, opts);
    if (error) throw error;
    return { url: data?.signedUrl || null, error: null };
  } catch (error) { return { url: null, error }; }
};

export const deleteWorkItemAttachment = async (id, filePath) => {
  try {
    await supabase.storage.from(WI_ATTACH_BUCKET).remove([filePath]);
    const { error } = await supabase.from('work_item_attachments').delete().eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (error) { return { error }; }
};

// ─────────────── 留言附件 ───────────────
// 留言附件是獨立分類(source='comment'),不會出現在主體附件清單,所以不會被人在
// 那邊刪掉、害留言裡的連結變死連結。只有兩條路會刪到:在留言內文裡移除、整則留言被刪。

export const WI_COMMENT_ATTACH_MAX_SIZE = 20 * 1024 * 1024; // 20MB

// 用黑名單而不是白名單:白名單比對的是瀏覽器給的 file.type,而瀏覽器對不認得的
// 副檔名給空字串或 application/octet-stream —— .fig / .sketch / .dwg / .mp4 全部會被
// 誤擋,而那些正是設計與 RD 常傳的東西。實際要擋的只有「開起來會執行」的檔案。
// 比對副檔名而非 MIME,因為 MIME 常常是空的,擋不到。
export const WI_COMMENT_ATTACH_BLOCKED_EXT = [
  'exe', 'dll', 'msi', 'scr', 'cpl', 'com', 'bat', 'cmd', 'ps1', 'vbs', 'vbe', 'wsf', 'hta',
  'jar', 'app', 'sh', 'bash', 'zsh', 'apk', 'deb', 'dmg', 'pkg', 'lnk',
  'html', 'htm', 'xhtml', 'mhtml', // 下載後在瀏覽器打開會帶著使用者 session 執行
];
const BLOCKED_MIME = ['text/html', 'application/xhtml+xml', 'application/x-msdownload', 'application/x-sh', 'application/x-executable'];

export const isAllowedCommentAttachment = (fileName, type) => {
  const ext = String(fileName || '').split('.').pop()?.toLowerCase() || '';
  if (WI_COMMENT_ATTACH_BLOCKED_EXT.includes(ext)) return false;
  return !BLOCKED_MIME.includes(String(type || '').toLowerCase());
};

/**
 * 上傳留言附件。此時留言還沒送出、拿不到 comment_id,先留 null,
 * 等 createWorkItemComment / updateWorkItemComment 解析內文再綁定(syncCommentAttachments)。
 */
export const uploadWorkItemCommentAttachment = async (workItemId, uploadedBy, file) => {
  try {
    if (file.size > WI_COMMENT_ATTACH_MAX_SIZE) throw new Error('檔案超過 20MB 上限');
    if (!isAllowedCommentAttachment(file.name, file.type)) throw new Error('為了安全,不接受可執行檔與網頁檔');
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `wi/${workItemId}/comments/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`;
    const { error: upErr } = await supabase.storage.from(WI_ATTACH_BUCKET).upload(filePath, file, { cacheControl: '3600', upsert: false });
    if (upErr) throw upErr;
    const { data, error } = await supabase.from('work_item_attachments').insert({
      work_item_id: workItemId, uploaded_by: uploadedBy, source: 'comment', comment_id: null,
      file_name: file.name, file_path: filePath, file_size: file.size, file_type: file.type,
    }).select('*').single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

/** 渲染留言時用:一次撈回內文引用到的附件 metadata(查不到 = 已移除,渲染成灰字)。 */
export const getWorkItemAttachmentsByIds = async (ids) => {
  const list = [...new Set((ids || []).map(Number))].filter(Boolean);
  if (!list.length) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      .from('work_item_attachments').select('id, file_name, file_path, file_size, file_type')
      .in('id', list);
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

const removeAttachmentRows = async (rows) => {
  if (!rows?.length) return;
  const paths = rows.map((r) => r.file_path).filter(Boolean);
  if (paths.length) await supabase.storage.from(WI_ATTACH_BUCKET).remove(paths);
  await supabase.from('work_item_attachments').delete().in('id', rows.map((r) => r.id));
};

/**
 * 重新決定這些附件的擁有者。
 *
 * 擁有者 = 目前仍引用它的留言中「最早建立的那一則」;沒有人引用了才連 Storage 檔案一起刪。
 *
 * 為什麼需要:內文可以被複製到任意多則留言(B 複製 A、C 又複製 B),但擁有權只有一個。
 * 直接刪檔的話,A 刪掉自己的留言就會讓 B / C 的 chip 莫名其妙變成「已移除」。
 * 改成每次有人放掉引用就重算,擁有權自然依建立順序往下傳:
 *   A 刪 → 傳給 B;若 B 已先刪 → 傳給 C;全部都不引用了 → 才真的刪檔。
 */
const reassignAttachmentOwners = async (workItemId, attachmentIds) => {
  const ids = [...new Set((attachmentIds || []).map(Number))].filter(Boolean);
  if (!ids.length || !workItemId) return;
  const { data: cms } = await supabase
    .from('work_item_comments').select('id, content')
    .eq('work_item_id', workItemId).order('id', { ascending: true });

  // attachmentId → 仍引用它的留言 id(依建立順序,[0] 就是繼承人)
  const refs = new Map(ids.map((id) => [id, []]));
  (cms || []).forEach((c) => {
    parseAttachmentIds(c.content).forEach((aid) => { if (refs.has(aid)) refs.get(aid).push(c.id); });
  });

  const orphanIds = [];
  for (const [aid, commentIds] of refs) {
    if (commentIds.length) {
      await supabase.from('work_item_attachments').update({ comment_id: commentIds[0] }).eq('id', aid);
    } else {
      orphanIds.push(aid);
    }
  }
  if (orphanIds.length) {
    const { data: rows } = await supabase.from('work_item_attachments')
      .select('id, file_path').in('id', orphanIds).eq('source', 'comment');
    await removeAttachmentRows(rows);
  }
};

/**
 * 留言送出 / 編輯後,依內文把留言附件對齊。
 *
 * 解析的是**系統自己產生的 markup**([檔名](wi-attachment:id)),不是使用者自由打的字,
 * 所以不會誤判 —— 這跟已經拿掉的 #編號 自動關聯不是同一回事。
 *   內文有、還沒綁 → 補上 comment_id
 *   已綁、但內文裡被刪掉 → 連 Storage 檔案一起清掉
 */
export const syncCommentAttachments = async (commentId, workItemId, content) => {
  try {
    const ids = parseAttachmentIds(content);
    if (ids.length) {
      await supabase.from('work_item_attachments')
        .update({ comment_id: commentId })
        .in('id', ids).eq('work_item_id', workItemId).eq('source', 'comment').is('comment_id', null);
    }
    // 這則留言原本擁有、但內文已經不再引用的 → 重算擁有權。
    // 不能直接刪:別的留言可能還引用著,刪了會害對方的 chip 變「已移除」。
    let q = supabase.from('work_item_attachments')
      .select('id').eq('comment_id', commentId).eq('source', 'comment');
    if (ids.length) q = q.not('id', 'in', `(${ids.join(',')})`);
    const { data: released } = await q;
    await reassignAttachmentOwners(workItemId, (released || []).map((r) => r.id));
    return { error: null };
  } catch (error) {
    console.warn('[workItems] sync comment attachments failed', error);
    return { error };
  }
};

/**
 * 草稿自癒:拖了檔案但留言最後沒送出(或關掉分頁 / 瀏覽器當掉)會留下沒綁定的附件。
 * 這種列不會出現在任何清單,不干擾使用者,只是佔一點 Storage。
 *
 * 在「開啟工作項目 Drawer」時掃,而不是「自己送出留言」時掃 —— 後者的死角是那個人
 * 再也不回那張工作項目留言就永遠不清。改成任何人開過這張單就會清掉過期草稿,
 * 涵蓋率高很多,而且不用養 cron。
 */
const COMMENT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const sweepStaleCommentDrafts = async (workItemId) => {
  try {
    if (!workItemId) return;
    const before = new Date(Date.now() - COMMENT_DRAFT_TTL_MS).toISOString();
    const { data: stale } = await supabase.from('work_item_attachments')
      .select('id')
      .eq('work_item_id', workItemId)
      .eq('source', 'comment').is('comment_id', null).lt('created_at', before);
    // 走 reassign 而不是直接刪:未綁定不一定是草稿,也可能是刪留言時解綁、
    // 正等著轉移給其他仍在引用它的留言。真的沒人引用才會被刪掉。
    await reassignAttachmentOwners(workItemId, (stale || []).map((r) => r.id));
  } catch (error) { console.warn('[workItems] sweep draft attachments failed', error); }
};

// ─────────────── 留言(討論) ───────────────
// ── 留言通知(mention + 追蹤者)────────────────────────────
// 比照 ticketComments.createComment 的 mention 通知,外加 watcher 機制:
//   追蹤者 = (assignees − 取消追蹤者) ∪ 主動追蹤者,排除留言者本人與已收 mention 通知者

const MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;
const parseMentions = (content) => {
  const ids = []; let m;
  const re = new RegExp(MENTION_REGEX.source, 'g');
  while ((m = re.exec(content || '')) !== null) ids.push(m[2]);
  return [...new Set(ids)];
};
// 通知信 / 摘要:mention 與附件 markup 都要還原成可讀文字,不要出現裸 markup
const stripMentionMarkup = (text) => stripAttachmentMarkup((text || '').replace(new RegExp(MENTION_REGEX.source, 'g'), '@$1'));

const getEmailsByUserIds = async (userIds) => {
  if (!userIds.length) return {};
  const { data } = await supabase.from('profiles').select('id, email').in('id', userIds);
  return Object.fromEntries((data || []).map((p) => [p.id, p.email]));
};

// 工作項目 deep link(email / Teams 的「查看詳情」)。
// 這裡只有 id、拿不到公司 slug,所以用 id 形式 ?wi=<id>(不帶 cid);開啟後
// BetaLayout 會自動正規化成 ?cid=<slug>&wino=<編號>。語義定義見 utils/workItemUrlParams。
const workItemUrl = (workItemId) => {
  const mainSystemUrl = import.meta.env.VITE_MAIN_SYSTEM_URL || '';
  return `${mainSystemUrl}/project/?v=new&wi=${workItemId}`;
};

/**
 * 留言後發通知(fire-and-forget 呼叫,失敗不影響留言本身)
 * @param {boolean} notifyFollowers - 新留言 true;編輯留言只補通知新增的 mention,false
 */
const notifyWorkItemComment = async ({ workItemId, authorId, content, mentionTargets, notifyFollowers }) => {
  const { data: wi } = await supabase
    .from('work_items')
    .select('title, work_item_no, projects:project_id (name), assignees:work_item_assignees(user_id)')
    .eq('id', workItemId).single();
  if (!wi) return;
  const projectName = wi.projects?.name || '';
  const label = wi.work_item_no
    ? `${projectName} #${wi.work_item_no} ${wi.title || ''}`.trim()
    : (wi.title || '');
  const snippet = stripMentionMarkup(content).slice(0, 100) + (content.length > 100 ? '...' : '');
  const url = workItemUrl(workItemId);

  // ── Mention 通知(偏好 key 沿用 'mentioned')──
  if (mentionTargets.length > 0) {
    const notifTitle = wi.work_item_no ? `在 ${projectName} #${wi.work_item_no} 的留言中提及了你` : '你在留言中被提及';
    const prefs = await isNotificationEnabledBatch(mentionTargets, 'mentioned');
    const inAppTargets = mentionTargets.filter((uid) => prefs[uid]?.in_app !== false);
    if (inAppTargets.length > 0) {
      await createBatchNotifications(inAppTargets.map((userId) => ({
        user_id: userId, type: 'mention', title: notifTitle, message: snippet,
        resource_type: 'work_item', resource_id: String(workItemId), actor_id: authorId,
      })));
    }
    const emailMap = await getEmailsByUserIds(mentionTargets);
    mentionTargets.forEach((uid) => {
      if (prefs[uid]?.email !== false && emailMap[uid]) {
        sendEmailNotification({
          to: emailMap[uid], subject: `[Sonice] 在 ${label} 的留言中提及了你`,
          html: buildEmailHtml({ title: notifTitle, message: stripMentionMarkup(content).slice(0, 200), ticketId: workItemId, ticketLabel: label, url }),
          type: 'mention',
        });
      }
      sendTeamsNotification({ user_id: uid, event_type: 'mentioned', title: notifTitle, message: snippet, ticketId: workItemId, targetEmail: emailMap[uid] || null, url });
    });
  }

  // ── 追蹤者通知(偏好 key 'work_item_comment')──
  if (!notifyFollowers) return;
  const { data: watchers } = await supabase
    .from('work_item_watchers').select('user_id, state').eq('work_item_id', workItemId);
  const muted = new Set((watchers || []).filter((w) => w.state === 'mute').map((w) => w.user_id));
  const explicitWatch = (watchers || []).filter((w) => w.state === 'watch').map((w) => w.user_id);
  const assigneeIds = (wi.assignees || []).map((a) => a.user_id);
  const followerTargets = [...new Set([...assigneeIds.filter((id) => !muted.has(id)), ...explicitWatch])]
    .filter((uid) => uid !== authorId && !mentionTargets.includes(uid));
  if (followerTargets.length === 0) return;

  const followTitle = wi.work_item_no ? `${projectName} #${wi.work_item_no} 有新留言` : `${wi.title || '工作項目'} 有新留言`;
  const prefs = await isNotificationEnabledBatch(followerTargets, 'work_item_comment');
  const inAppTargets = followerTargets.filter((uid) => prefs[uid]?.in_app !== false);
  if (inAppTargets.length > 0) {
    await createBatchNotifications(inAppTargets.map((userId) => ({
      user_id: userId, type: 'work_item_comment', title: followTitle, message: snippet,
      resource_type: 'work_item', resource_id: String(workItemId), actor_id: authorId,
    })));
  }
  const emailMap = await getEmailsByUserIds(followerTargets);
  followerTargets.forEach((uid) => {
    if (prefs[uid]?.email !== false && emailMap[uid]) {
      sendEmailNotification({
        to: emailMap[uid], subject: `[Sonice] ${followTitle} — ${label}`,
        html: buildEmailHtml({ title: followTitle, message: stripMentionMarkup(content).slice(0, 200), ticketId: workItemId, ticketLabel: label, url }),
        type: 'work_item_comment',
      });
    }
    sendTeamsNotification({ user_id: uid, event_type: 'work_item_comment', title: followTitle, message: snippet, ticketId: workItemId, targetEmail: emailMap[uid] || null, url });
  });
};

// ── 追蹤/取消追蹤(留言通知的 per-item 覆寫)────────────────
export const getMyWorkItemWatchState = async (workItemId, userId) => {
  try {
    if (!userId) return { data: null, error: null };
    const { data, error } = await supabase
      .from('work_item_watchers').select('*')
      .eq('work_item_id', workItemId).eq('user_id', userId).maybeSingle();
    if (error) throw error;
    return { data: data || null, error: null };
  } catch (error) { return { data: null, error }; }
};

/**
 * 設定追蹤狀態。與預設一致(指派者追蹤 / 非指派者不追蹤)→ 刪除覆寫紀錄;
 * 偏離預設 → upsert 'watch' / 'mute'。回傳最新 row(回到預設時為 null)。
 */
export const setWorkItemWatchState = async (workItemId, userId, watching, isAssignee) => {
  try {
    const isDefault = watching === !!isAssignee;
    if (isDefault) {
      const { error } = await supabase.from('work_item_watchers')
        .delete().eq('work_item_id', workItemId).eq('user_id', userId);
      if (error) throw error;
      return { data: null, error: null };
    }
    const { data, error } = await supabase.from('work_item_watchers')
      .upsert(
        { work_item_id: workItemId, user_id: userId, state: watching ? 'watch' : 'mute', updated_at: new Date().toISOString() },
        { onConflict: 'work_item_id,user_id' },
      )
      .select('*').single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

export const getWorkItemComments = async (workItemId) => {
  try {
    const { data, error } = await supabase
      .from('work_item_comments').select('*')
      .eq('work_item_id', workItemId).order('created_at', { ascending: true });
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

export const createWorkItemComment = async ({ work_item_id, author_id, content }) => {
  try {
    const { data, error } = await supabase
      .from('work_item_comments').insert({ work_item_id, author_id, content }).select('*').single();
    if (error) throw error;
    // 內文引用到的留言附件綁到這則留言(過期草稿的清理在開 Drawer 時做)
    await syncCommentAttachments(data.id, work_item_id, content);
    // mention + 追蹤者通知(fire-and-forget,失敗不影響留言)
    notifyWorkItemComment({
      workItemId: work_item_id, authorId: author_id, content,
      mentionTargets: parseMentions(content).filter((uid) => uid !== author_id),
      notifyFollowers: true,
    }).catch((e) => console.warn('[workItems] comment notify failed', e));
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

export const updateWorkItemComment = async (id, content) => {
  try {
    // 舊內容供 mention 比對(比照 ticket updateComment:編輯只補通知「新增的」mention)
    const { data: old } = await supabase
      .from('work_item_comments').select('work_item_id, author_id, content').eq('id', id).single();
    const { data, error } = await supabase
      .from('work_item_comments')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id).select('*').single();
    if (error) throw error;
    // 編輯後重新對齊:新引用的補綁,被刪掉的連 Storage 檔案一起清
    await syncCommentAttachments(id, data.work_item_id, content);
    const oldMentions = new Set(parseMentions(old?.content));
    const newMentions = parseMentions(content).filter((uid) => !oldMentions.has(uid) && uid !== old?.author_id);
    if (old && newMentions.length > 0) {
      notifyWorkItemComment({
        workItemId: old.work_item_id, authorId: old.author_id, content,
        mentionTargets: newMentions, notifyFollowers: false,
      }).catch((e) => console.warn('[workItems] comment notify failed', e));
    }
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

export const deleteWorkItemComment = async (id) => {
  try {
    const { data: cm } = await supabase.from('work_item_comments')
      .select('work_item_id').eq('id', id).maybeSingle();
    const { data: owned } = await supabase.from('work_item_attachments')
      .select('id').eq('comment_id', id).eq('source', 'comment');
    const ownedIds = (owned || []).map((r) => r.id);

    // 一定要先解綁再刪留言:comment_id 是 ON DELETE CASCADE,留言一刪附件列就跟著消失,
    // 就來不及轉移給還在引用它的其他留言了。
    if (ownedIds.length) {
      await supabase.from('work_item_attachments').update({ comment_id: null }).in('id', ownedIds);
    }
    const { error } = await supabase.from('work_item_comments').delete().eq('id', id);
    if (error) throw error;
    // 還有人引用 → 轉移給最早的那則;沒人引用 → 連 Storage 檔案一起刪
    await reassignAttachmentOwners(cm?.work_item_id, ownedIds);
    return { error: null };
  } catch (error) { return { error }; }
};

// ─────────────── Delivery Logs(交付紀錄) ───────────────
export const getWorkItemDeliveryLogs = async (workItemId) => {
  try {
    const { data, error } = await supabase
      .from('work_item_delivery_logs')
      .select('*, attachments:work_item_attachments(id, file_name, file_path, file_size, file_type)')
      .eq('work_item_id', workItemId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) { return { data: [], error }; }
};

export const createWorkItemDeliveryLog = async (data) => {
  try {
    const { data: row, error } = await supabase
      .from('work_item_delivery_logs')
      .insert({
        work_item_id: data.work_item_id,
        user_id: data.user_id,
        label: data.label,
        version: data.version || null,
        environment: data.environment || null,
        note: data.note || null,
        company_id: data.company_id,
        department_id: data.department_id,
      })
      .select('*').single();
    if (error) throw error;
    return { data: row, error: null };
  } catch (error) { return { data: null, error }; }
};

export const updateWorkItemDeliveryLog = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('work_item_delivery_logs')
      .update({
        label: updates.label,
        version: updates.version || null,
        environment: updates.environment || null,
        note: updates.note || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id).select('*').single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) { return { data: null, error }; }
};

export const deleteWorkItemDeliveryLog = async (id) => {
  try {
    const { error } = await supabase
      .from('work_item_delivery_logs')
      .update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (error) { return { error }; }
};

// 我的工作項目(跨專案,指派給我的)— 抄舊 searchTickets my-tasks 模式:
// 先查 work_item_assignees 撈我的 work_item_id → 再查 work_items。
// 我的工作項目:指派給我的(標 _mine) + 這些子項的母項(未指派給我者標 _context,供樹狀脈絡)
export const getMyWorkItems = async (userId, { companyId } = {}) => {
  try {
    if (!userId) return { data: [], error: null };
    const { data: aData, error: aErr } = await supabase
      .from('work_item_assignees').select('work_item_id').eq('user_id', userId);
    if (aErr) throw aErr;
    const ids = [...new Set((aData || []).map((a) => a.work_item_id))];
    if (!ids.length) return { data: [], error: null };

    const SEL = '*, projects:project_id (id, name, color), assignees:work_item_assignees(user_id, assigned_at)';
    let q = supabase.from('work_items').select(SEL)
      .in('id', ids)
      .is('deleted_at', null)
      .neq('status', 'closed'); // 對齊舊 my-tasks:只排除 closed,其餘都顯示
    if (companyId) q = q.eq('company_id', companyId);
    q = q.order('project_id', { ascending: true })
         .order('priority', { ascending: false })
         .order('work_item_no', { ascending: true });
    const { data, error } = await q;
    if (error) throw error;
    const mine = (data || []).map((it) => ({ ...it, _mine: true }));

    // 補抓「未在清單、但有我子項掛在其下」的母項當脈絡
    const haveIds = new Set(mine.map((i) => i.id));
    const parentIds = [...new Set(mine.filter((i) => i.parent_id).map((i) => i.parent_id))]
      .filter((pid) => !haveIds.has(pid));
    let context = [];
    if (parentIds.length) {
      const { data: pData } = await supabase.from('work_items').select(SEL)
        .in('id', parentIds).is('deleted_at', null);
      context = (pData || []).map((it) => ({ ...it, _context: true }));
    }
    return { data: [...mine, ...context], error: null };
  } catch (error) { return { data: [], error }; }
};

// DailyReport 專用:比照 getTicketsForDailyReport 撈某成員的 work_item
// 規則:目前被指派的非 closed + 今日 closed + 今日被 unassign
export const getWorkItemsForDailyReport = async (userId, {
  companyId = null,
  todayUnassignedWorkItemIds = [],
} = {}) => {
  try {
    if (!userId) return { data: [], error: null };
    const { data: aData, error: aErr } = await supabase
      .from('work_item_assignees').select('work_item_id').eq('user_id', userId);
    if (aErr) throw aErr;
    const assignedIds = (aData || []).map((a) => a.work_item_id);
    const allRelevantIds = [...new Set([...assignedIds, ...todayUnassignedWorkItemIds])];
    if (!allRelevantIds.length) return { data: [], error: null };

    // project join 補 icon/external_url/description,對齊 ticket 的專案分組 UI
    let q = supabase.from('work_items')
      .select('*, projects:project_id (id, name, color, icon, external_url, description), group:group_id (id, name, sort_order), assignees:work_item_assignees (user_id, assigned_at)')
      .in('id', allRelevantIds)
      .is('deleted_at', null)
      // 頂層依編號遞減(新的在前),對齊工作項目列表;子項的順序在前端用
      // compareWorkItemSiblings 決定,跟 Drawer 一致。原本完全沒有 order,連母項
      // 之間的順序都是不確定的。
      .order('work_item_no', { ascending: false, nullsFirst: false })
      .order('assigned_at', { referencedTable: 'work_item_assignees', ascending: true });
    if (companyId) q = q.eq('company_id', companyId);
    const { data, error } = await q;
    if (error) throw error;

    // 過濾:closed 只在「今日 closed」或「今日 unassign」保留(對齊 ticket 規則)
    const today = new Date().toISOString().slice(0, 10);
    const unassignedSet = new Set(todayUnassignedWorkItemIds);
    const filtered = (data || []).filter((wi) => {
      if (wi.status !== 'closed') return true;
      if (unassignedSet.has(wi.id)) return true;
      return wi.updated_at?.slice(0, 10) === today;
    });

    // 子項指派給我、但母項沒有 → 母項不在結果裡,子項會變孤兒沒有脈絡。
    // 補撈那些母項並標記 _context:日報只用它們當標題分組,不可編輯、不計入統計。
    const haveIds = new Set(filtered.map((w) => w.id));
    const missingParentIds = [...new Set(
      filtered.filter((w) => w.parent_id && !haveIds.has(w.parent_id)).map((w) => w.parent_id),
    )];
    let contextParents = [];
    if (missingParentIds.length) {
      const { data: pData } = await supabase.from('work_items')
        .select('*, projects:project_id (id, name, color, icon, external_url, description), group:group_id (id, name, sort_order), assignees:work_item_assignees (user_id, assigned_at)')
        .in('id', missingParentIds).is('deleted_at', null);
      contextParents = (pData || []).map((w) => ({ ...w, _context: true }));
    }
    return { data: [...filtered, ...contextParents], error: null };
  } catch (error) {
    console.error('Error fetching work items for daily report:', error);
    return { data: null, error };
  }
};

// 行事曆:撈某公司所有「有日期」的 work_item(含子項),供月曆跨天橫桿。
// 只撈輕量欄位;月份範圍過濾在前端做,資料量大時再改成帶 from/to 進 DB。
export const getWorkItemsForCalendar = async ({ companyId } = {}) => {
  try {
    let q = supabase.from('work_items')
      .select('id, work_item_no, title, status, priority, type, start_date, end_date, parent_id, project_id, projects:project_id (id, name, color), assignees:work_item_assignees(user_id, assigned_at)')
      .is('deleted_at', null)
      .or('start_date.not.is.null,end_date.not.is.null');
    if (companyId) q = q.eq('company_id', companyId);
    const { data, error } = await q;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching work items for calendar:', error);
    return { data: [], error };
  }
};

// 看板檢視:全公司父層工作項目(不含子項),依狀態分欄用
export const getWorkItemsForBoard = async ({ companyId, projectIds } = {}) => {
  try {
    let q = supabase.from('work_items')
      .select('id, work_item_no, title, status, priority, type, parent_id, project_id, projects:project_id (id, name, color), assignees:work_item_assignees(user_id, assigned_at)')
      .is('deleted_at', null)
      .is('parent_id', null)
      .order('display_order', { ascending: true })
      .order('work_item_no', { ascending: true });
    if (companyId) q = q.eq('company_id', companyId);
    if (projectIds?.length) q = q.in('project_id', projectIds);
    const { data, error } = await q;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching work items for board:', error);
    return { data: [], error };
  }
};

// 批次改狀態(看板拖曳單筆時也可用)已於上方 updateWorkItemsStatus 提供

// 把 flat list 依 parent_id 組成樹(回傳頂層 + 每個節點帶 children)
export const buildWorkItemTree = (items) => {
  const byId = new Map();
  items.forEach((it) => byId.set(it.id, { ...it, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  // 子項在資料庫的排序鍵上完全打平(無 work_item_no、display_order 全是 0),
  // 順序等於 Postgres 回傳什麼就什麼、改過的會跳到最後 —— 這裡統一給穩定排序。
  // 頂層不用處理:work_item_no 在公司內唯一,查詢已經是確定的。
  byId.forEach((node) => {
    if (node.children.length > 1) node.children.sort(compareWorkItemSiblings);
  });
  return roots;
};
